<template>
  <div
    :class="{
      'container mx-auto mt-6': true,
    }"
  >
    <h2>
      {{ title }}
    </h2>
    <p
      :class="{
        'text-center text-xs leading-7 text-www-color-neutral-medium-dark md:text-15px': true,
      }"
    >
      {{ subtitle }}
    </p>

    <slot />
  </div>
</template>

<script lang="ts" setup>
import { FieldHtmlNameUtil } from '@onshape/www-inertia';
import { toRefs } from 'vue';
import type UnifiedSignupSessionInfo from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupSessionInfo';
import type ProDetails2SignupScreen from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/screens/ProDetails2SignupScreen';
import type { UssProPlan } from '~/sanity/types';

const { signupScreen } = defineProps<
  UssProPlan & {
    signupScreen: ProDetails2SignupScreen;
    sessionInfo: UnifiedSignupSessionInfo;
  }
>();

// Get field references
const geoCountryField = signupScreen.getField(FieldHtmlNameUtil.geoCountry);
const itarRegField = signupScreen.getField(FieldHtmlNameUtil.itarReg);
const howManyUserToTrialField = signupScreen.getField(FieldHtmlNameUtil.howManyUserToTrial);
const emailField = signupScreen.getField(FieldHtmlNameUtil.email);
const addressFields = [
  signupScreen.getField(FieldHtmlNameUtil.addEmail1),
  signupScreen.getField(FieldHtmlNameUtil.addEmail2),
  signupScreen.getField(FieldHtmlNameUtil.addEmail3),
  signupScreen.getField(FieldHtmlNameUtil.addEmail4),
  signupScreen.getField(FieldHtmlNameUtil.addEmail5),
];

// Helper to clear added email fields
const clearAddedEmailFields = () => {
  addressFields.forEach((field) => {
    field.val = '';
    field.componentName = 'hidden-field';
    field.isRequired = false;
  });
};

// Watch geoCountry to show/hide ITAR field for US users
watch(
  geoCountryField,
  (newVal) => {
    if (`${newVal.val}`.toLocaleLowerCase() === 'united states') {
      itarRegField.componentName = itarRegField.initialComponentName;
      itarRegField.isRequired = true;
    } else {
      itarRegField.componentName = 'hidden-field';
      itarRegField.isRequired = false;
      itarRegField.val = '';
    }
  },
  { immediate: true }
);

// Watch address values to prevent duplicate email entries
const usersEmail = emailField.val;
const addressValues = addressFields.map((field) => toRefs(field).val);
watch(addressValues, (newVal) => {
  const findIndex = newVal.findIndex(
    (val) => `${val}`.toLocaleLowerCase() === `${usersEmail}`.toLocaleLowerCase()
  );
  if (findIndex !== -1) {
    addressFields[findIndex]!.val = '';
  }
});

// Watch howManyUserToTrial to manage email address fields
const howManyUserToTrialVal = toRefs(howManyUserToTrialField).val;
clearAddedEmailFields();
watch(
  howManyUserToTrialVal,
  (newVal) => {
    if (!newVal) {
      clearAddedEmailFields();
      return;
    }
    const newValNum = parseInt(`${newVal}`, 10);
    const updatedAddressesValues: string[] = [];
    for (let i = 0; i < addressFields.length; i += 1) {
      if (updatedAddressesValues.length >= newValNum || !addressFields[i]?.val) {
        continue;
      }
      updatedAddressesValues.push(addressFields[i]?.val as string);
    }
    clearAddedEmailFields();
    for (let i = 0; i < newValNum; i += 1) {
      const currentAddress = addressFields[i];
      if (!currentAddress) {
        continue;
      }
      currentAddress.componentName = currentAddress.initialComponentName;
      currentAddress.isRequired = false;
      if (updatedAddressesValues.length > 0) {
        const fieldValue = updatedAddressesValues.shift();
        if (fieldValue) {
          currentAddress.val = fieldValue;
        }
      }
    }
  },
  { immediate: true }
);
</script>

<style></style>
